import { Component } from 'react';
import { Card } from 'react-bootstrap';
import Converter from "../../converter";
import SessionContext from "../../session/context";

export default class WidgetBase extends Component {
  static contextType = SessionContext;
  
  constructor(props) {
    super(props);
    this.subscribedAliases = new Set();
    this.discoveredAliases = new Set();
    this.cleanupFns = {};
  }

  loadObjectValue(component, route, type) {
    const alias = Converter.getComponentPortValueData(component, route);
    if ( !alias ) {
      /*
       * 2024-11-20: If value is not yet bound to a specific session alias
       * we just ignore it silently, instead of displaying an error.
       */
      return [null, null];
    }
    
    // Use context which is SessionManager
    const sessionManager = this.context;
    
    // Also support static usage if passed explicitly (backward compat or non-context usage)
    const manager = sessionManager || arguments[0];
    
    if ( !manager ) {
      return [null, "Session Manager is not available."]; 
    }

    if ( !manager.session ) {
      return [null, "Session is not selected."];
    }

    const value = manager.getSessionData(alias);
    
    // Register alias for subscription
    this.discoveredAliases.add(alias);

    if ( !value ) {
      return [null, `"${alias}" data is not available (not loaded yet or not present within selected session).`];
    }

    const valueType = value["type"];
    if ( type !== valueType ) {
      return [null, `Specified alias points at an object of "${valueType}" type. "${type}" object expected.`];
    }

    return [value, null];
  }

  componentDidMount() {
    this.updateSubscriptions();
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentDidUpdate() {
    this.updateSubscriptions();
  }

  componentWillUnmount() {
    // Unsubscribe all
    for (const alias in this.cleanupFns) {
      this.cleanupFns[alias]();
    }
    this.cleanupFns = {};
    this.subscribedAliases.clear();
    
    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
  }
  
  updateSubscriptions() {
    const manager = this.context;
    if (!manager) return;

    // Unsubscribe from stale
    for (const alias of this.subscribedAliases) {
      if (!this.discoveredAliases.has(alias)) {
        if (this.cleanupFns[alias]) {
          this.cleanupFns[alias]();
          delete this.cleanupFns[alias];
        }
        this.subscribedAliases.delete(alias);
      }
    }

    // Subscribe to new
    for (const alias of this.discoveredAliases) {
      if (!this.subscribedAliases.has(alias)) {
        this.cleanupFns[alias] = manager.subscribe(alias, () => {
          this.forceUpdate();
        });
        this.subscribedAliases.add(alias);
      }
    }
    
    // Reset discovered for next render
    this.discoveredAliases.clear();
  }

  renderBody() {
    return (
      <Card.Body>
        Widget content implementation is missing.
      </Card.Body>
    );
  }

  getClassName() {
    return "";
  }

  renderContent() {
    // Widget dimensions
    const width = Converter.getComponentPortValueData(this.props.data.component, "width");
    const height = Converter.getComponentPortValueData(this.props.data.component, "height");

    return (
      <Card.Body className="nodrag nowheel" style={{
        "width": `${width}px`,
        "height": `${height}px`,
        "max-height": `${height}px`,
        "overflow": "scroll"
      }}>
        {this.renderBody()}
      </Card.Body>
    );
  }

  render() {
    // Base rendering
    // Clear discovered aliases to restart discovery for this render pass
    // (Note: set cleared in updateSubscriptions is for post-render, this/here logic depends on how render cycle works.
    // Actually, we should accumulate during render, and process in DidUpdate.
    // So we need to make sure discoveredAliases is empty before starting accumulation?
    // But Render might be called multiple times.
    // Let's rely on DidUpdate clearing it, but we need to ensure we don't accumulate duplicates (Set handles that).
    // Better: Clear it at the BEGINNING of render? No, because renderContent calls renderBody.
    // We can clear it here, before renderContent.
    this.discoveredAliases.clear();

    return (
      <Card className={this.getClassName()}>
        <Card.Header>
          <span className="playground-widget-name">
            {this.props.data.component.name}
          </span>
          <span className="float-right text-muted">
            {this.props.data.component.type}
          </span>
        </Card.Header>
        {this.renderContent()}
      </Card>
    );
  }
}
